/**
 * Requests week arithmetic — the TS twin of the SQL functions `vn_week_key` /
 * `vn_week_start` (migration 060_requests_schema.sql). Weeks start Monday 00:00 in
 * Vietnam time and are numbered by ISO-8601 (`2026-W42`; the ISO year can differ
 * from the calendar year around New Year, e.g. 2027-01-01 is `2026-W53`).
 *
 * Vietnam has no daylight-saving time (fixed UTC+07:00 since 1975), so a constant
 * offset is exact and no date library is needed. The DB is the source of truth for
 * every stored `week_key` — this module is for display and for building query
 * arguments; `test/week.spec.ts` pins it against the SQL at the year/day edges.
 */

export const VN_TZ = 'Asia/Ho_Chi_Minh';

const VN_OFFSET_MS = 7 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_KEY_RE = /^(\d{4})-W(\d{2})$/;

/** A Date whose UTC fields read as the Vietnam wall clock of `d`. */
function vnWall(d: Date): Date {
  return new Date(d.getTime() + VN_OFFSET_MS);
}

/** ISO week key (`YYYY-Www`) of the instant `date`, in Vietnam time. */
export function weekKeyOf(date: Date | string | number): string {
  const t = new Date(date);
  if (Number.isNaN(t.getTime())) throw new Error(`weekKeyOf: invalid date ${String(date)}`);
  const wall = vnWall(t);
  // ISO: the week belongs to the year that contains its Thursday.
  const dow = (wall.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  const thursday = Date.UTC(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate() - dow + 3);
  const isoYear = new Date(thursday).getUTCFullYear();
  const jan1 = Date.UTC(isoYear, 0, 1);
  const week = 1 + Math.floor((thursday - jan1) / (7 * DAY_MS));
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
}

/** The current week key (Vietnam time). */
export function currentWeekKey(now: Date = new Date()): string {
  return weekKeyOf(now);
}

/**
 * The instant range of a week key: Monday 00:00 VN (inclusive) → the next Monday
 * 00:00 VN (exclusive). Throws on a malformed key or a week the ISO year lacks (W53).
 */
export function weekRange(key: string): { start: Date; end: Date } {
  const m = WEEK_KEY_RE.exec(key);
  if (!m) throw new Error(`weekRange: malformed week key "${key}" (want YYYY-Www)`);
  const year = Number(m[1]);
  const week = Number(m[2]);
  // Monday of ISO week 1 = the Monday on/before Jan 4.
  const jan4 = Date.UTC(year, 0, 4);
  const jan4Dow = (new Date(jan4).getUTCDay() + 6) % 7;
  const week1MondayWall = jan4 - jan4Dow * DAY_MS;
  const startWall = week1MondayWall + (week - 1) * 7 * DAY_MS;
  const start = new Date(startWall - VN_OFFSET_MS);
  if (week < 1 || weekKeyOf(start) !== key) {
    throw new Error(`weekRange: week ${week} does not exist in ISO year ${year}`);
  }
  return { start, end: new Date(start.getTime() + 7 * DAY_MS) };
}
